import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux"; // To call Action Creators
import { bindActionCreators } from "redux";
import { actionCreators } from "../../state";
import { State } from '../../state/reducers';
import _ from 'lodash';
import "./Homepage.scss";

import logoUrl from "../../assets/logo-light.png";
import chromeLogoUrl from "../../assets/chrome.png";
import firefoxLogoUrl from "../../assets/firefox.png";

// import global styles
import { viewSize, panelStyle } from "../../types/style.global";

// import customized components
import MainView from "./MainView";
import QueryView from "./SideView/QueryView";
import GrantView from "./SideView/GrantView";
import PIView from "./SideView/PIView";
import ImpactStatisticsView from "./SideView/ImpactStatisticsView";
import ImpactEntityView from "./SideView/ImpactEntityView";

// import AntD component
import {
  InfoCircleFilled,
  QuestionCircleFilled,
} from '@ant-design/icons';
import { Select, Layout, Drawer, Modal, Tour, Button } from "antd";
import type { TourProps } from 'antd';
import { buildTourSteps } from "../../tour_data";
const { Option } = Select;
const { Header, Content } = Layout;

function Homepage() {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);
  const [stateName, setStateName] = useState<string>("Select");
  const [universityName, setUniversityName] = useState<string>("Select");
  const [scienceField, setScienceField] = useState<string>("Select");
  const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false);
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
  const [tourStep, setTourStep] = useState<number>(0);

  // Tour anchors
  const introRef = useRef<HTMLDivElement | null>(null);
  const queryRef = useRef<HTMLDivElement | null>(null);
  const grantRef = useRef<HTMLDivElement | null>(null);
  const piRef = useRef<HTMLDivElement | null>(null);
  const impactTypeRef = useRef<HTMLDivElement | null>(null);
  const landscapeRef = useRef<HTMLDivElement | null>(null);
  const entityRef = useRef<HTMLDivElement | null>(null);
  const historicalRef = useRef<HTMLDivElement | null>(null);
  const helpRef = useRef<HTMLDivElement | null>(null);

  const tourSteps: TourProps['steps'] = useMemo(() => buildTourSteps({
    introRef,
    queryRef,
    grantRef,
    piRef,
    impactTypeRef,
    landscapeRef,
    entityRef,
    historicalRef,
    helpRef,
  }), [introRef, queryRef, grantRef, piRef, impactTypeRef, landscapeRef, entityRef, historicalRef, helpRef]);

  // Tour 媒体映射（在弹窗内显示，不再覆盖视图）
  const mediaByStep = useMemo(() => ({
    1: { type: 'video', src: '/tour_references/v3-20250909/1.mp4' },
    2: { type: 'video', src: '/tour_references/v3-20250909/2.mp4' },
    3: { type: 'video', src: '/tour_references/v3-20250909/3.mp4' },
    4: { type: 'video', src: '/tour_references/v3-20250909/4.mp4' },
    5: { type: 'video', src: '/tour_references/v3-20250909/5.mp4' },
    6: { type: 'video', src: '/tour_references/v3-20250909/6.mp4' },
    7: { type: 'video', src: '/tour_references/v3-20250909/7.mp4' },
    8: { type: 'video', src: '/tour_references/v3-20250909/8.mp4' },
    9: { type: 'video', src: '/tour_references/v3-20250909/0915-compressed.mp4' },
  } as Record<number, { type: 'image' | 'video'; src: string }>), []);

  const enhancedSteps: TourProps['steps'] = useMemo(() => {
    const base = tourSteps || [];
    return base.map((step, index) => {
      const media = (() => {
        const m = mediaByStep[index];
        if (!m) return null;

        // 特殊处理第7步（Prediction Mode）- 包含两段视频和两段文字
        if (index === 7) { // 第7步（0-based index）
          return (
            <div>
              {/* 第一段视频：Landscape View */}
              <div className="tour-media" style={{ marginTop: 8 }}>
                <video src={mediaByStep[7].src} controls autoPlay loop muted playsInline />
              </div>
              {/* 第一段文字 */}
              <div style={{ marginTop: 8, marginBottom: 8 }}>
                <p>Switch to prediction mode to explore future investment opportunities. </p>
                <p><span style={{ color: '#7F5539', fontWeight: 'bold' }}>Click</span> an impact topic node to display the distribution of recent high-prediction-score grants (highlighted with <span style={{ color: '#AE7BAC', fontWeight: 'bold' }}>a purple ring</span>) and the corresponding PI information (listed in the PI List). </p>
                <p><span style={{ color: '#7F5539', fontWeight: 'bold' }}>Hover</span> over a grant node to view a word cloud of these high-prediction-score grants.</p>
                {/* <p>Explore the current impact landscape to understand the distribution of scientific outcomes and their relationships.</p> */}
              </div>
              {/* 第二段视频：PI List */}
              <div className="tour-media" style={{ marginTop: 8 }}>
                <video src={mediaByStep[4].src} controls autoPlay loop muted playsInline />
              </div>
              {/* 第二段文字 */}
              <div style={{ marginTop: 8, marginBottom: 8 }}>
                <p>Browse PIs associated with these high-prediction-score grants. <span style={{ color: '#AE7BAC', fontWeight: 'bold' }}>PI names highlighted in purple</span> indicate researchers who have not yet received funding from the selected agency (missing opportunities).</p>
              </div>
            </div>
          );
        }

        if (index === 9) {
          return (
            <></>
          );
        }

        // 其他步骤的正常处理
        if (m.type === 'video') {
          return (
            <div className="tour-media" style={{ marginTop: 8 }}>
              <video src={m.src} controls autoPlay loop muted playsInline />
            </div>
          );
        }
        return (
          <div className="tour-media" style={{ marginTop: 8 }}>
            <img src={m.src} alt={step?.title as string} />
          </div>
        );
      })();

      const description = (
        <div>
          {media}
          {/* 对于第7步，不显示原来的 description，只显示自定义的两段文字 */}
          {index !== 7 && (
            <div style={{ marginTop: 8 }}>
              {step?.description}
            </div>
          )}
        </div>
      );

      return {
        ...step,
        description,
        nextButtonProps: {
          ...(step as any).nextButtonProps,
          children: index === base.length - 1 ? 'Finish' : 'Next',
          type: 'default',
        },
        prevButtonProps: index > 0 ? { children: 'Prev', type: 'default' } : undefined,
      };
    });
  }, [tourSteps, mediaByStep]);

  // 每次页面加载都自动开启 Tour，等待目标节点挂载后再打开
  useEffect(() => {
    const refsList = [introRef, queryRef, grantRef, piRef, impactTypeRef, landscapeRef, entityRef, historicalRef];
    const tryOpen = () => {
      const allReady = refsList.every(r => {
        const el = r.current;
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
      // const allReady = refsList.every(r => r.current);
      if (allReady) {
        setTourStep(0);
        setIsTourOpen(true);
      } else {
        window.setTimeout(tryOpen, 200);
      }
    };
    window.setTimeout(tryOpen, 0);
  }, []);

  // store
  const dispatch = useDispatch();
  const {
    getBackendData, // test
    // //loading related
    // updateResearcherOverviewLoading,
  } = bindActionCreators(actionCreators, dispatch);

  const userName: string = useSelector((state: State) => state.uiReducer?.authState.user) as string;

  // 1. styles
  const onCollapse = () => {
    setCollapsed(!collapsed);
  };
  const onCloseDrawer = () => {
    setShowDrawer(false);
  };
  const onChangeDrawer = () => {
    setShowDrawer(!showDrawer);
  };

  // 2. data
  const handleUniversityChange = (value: string) => {
    setUniversityName(value)
  }
  const handleScienceFieldChange = (value: string) => {
    setScienceField(value)
  }
  const handleStateChange = (value: string) => {
    setStateName(value)
  }

    // Help Modal handlers
  const showHelpModal = () => {
    setIsHelpModalOpen(true);
  };

  const handleHelpModalOk = () => {
    setIsHelpModalOpen(false);
  };

  const handleHelpModalCancel = () => {
    setIsHelpModalOpen(false);
  };

  return (
    <div className="App">
      <Layout className="layout">
        <Header className="header">
          <div className="system-logo" ref={introRef}>
            <img src={logoUrl} alt="" style={{ height: 45, padding: 8 }} />
            <span style={{letterSpacing: 1}} className="logo-name">Funding the Frontier</span>
            <span className="title">Visualizing the Broad Impact of Science and Science Funding</span>
          </div>
          <div className="data-selection-drawer">
            {/* <Drawer
              height={255}
              placement="top"
              closable={true}
              mask={false}
              onClose={onCloseDrawer}
              open={showDrawer}
              getContainer={false}
            >
              <div className="selector">
                <div className="selector-name">State:&nbsp;</div>
                <Select
                  defaultValue="Select"
                  style={{ width: 200 }}
                  onChange={handleStateChange}
                >
                  <Option value="All">All</Option>
                  <Option value="IL">IL</Option>
                  <Option value="DC">DC</Option>
                </Select>
              </div>
              <div className="selector">
                <div className="selector-name">University:&nbsp;</div>
                <Select
                  defaultValue="Select"
                  style={{ width: 200 }}
                  onChange={handleUniversityChange}
                >
                  <Option value="All">All</Option>
                  <Option value="NU">NU</Option>
                  <Option value="UChicago">UChicago</Option>
                </Select>
              </div>
              <div className="selector">
                <div className="selector-name">Science Field:&nbsp;</div>
                <Select
                  defaultValue="Select"
                  style={{ width: 200 }}
                  onChange={handleScienceFieldChange}
                >
                  <Option value="All">All</Option>
                  <Option value="VIS/HCI/Graphics">VIS/HCI/Graphics</Option>
                  <Option value="Deep Learning">Deep Learning</Option>
                  <Option value="Chemistry">Chemistry</Option>
                  <Option value="Medical">Medical</Option>
                  <Option value="Material Science">Material Science</Option>
                </Select>
              </div>
            </Drawer> */}
            <button className="drawer-button" onClick={onChangeDrawer}>
              {/* <div className="dataset-name button-div">
                <span className="span-text">ORGANIZATION: </span>
                <span className="data-text"> FunderA </span>
              </div> */}
              <div className="group-criteria button-div">
                <span className="span-text">USER: </span>
                <span className="data-text">  {userName} </span>
                {/* <span className="data-text"> {universityName}</span> */}
              </div>
              <div className="group-criteria button-div" ref={helpRef}>
                <span className="span-text">HELP: </span>
                <span className="data-text" onClick={showHelpModal}> <InfoCircleFilled /> </span>
                {/* <span className="data-text"> {universityName}</span> */}
              </div>
              <div className="group-criteria button-div">
                <span className="span-text">USER GUIDE: </span>
                <span className="data-text" onClick={() => setIsTourOpen(true)}> <QuestionCircleFilled /> </span>
              </div>
            </button>
          </div>
        </Header>
        <Layout className="body">
          <Content className="content">
            <div className="view-layout">
              <div className="side-two-views">
                <div ref={queryRef} className="tour-host">
                  <QueryView viewParam={viewSize.queryView} panelParam={panelStyle} viewName={"Query View"} />
                </div>
                <div ref={grantRef} className="tour-host">
                  <GrantView viewParam={viewSize.grantView} panelParam={panelStyle} viewName={"Grant View"} />
                </div>
                <div ref={piRef} className="tour-host">
                  <PIView viewParam={viewSize.PIView} panelParam={panelStyle} viewName={"PI View"} />
                </div>
              </div>
              <div className='main-view' ref={landscapeRef}>
              {/* <div className="side-two-views"> */}
                <MainView
                  viewParam={viewSize.mainView}
                  panelParam={panelStyle}
                  viewName={"Impact Landscape View"}
                  historicalRef={historicalRef}
                />
              </div>
              <div className="side-two-views-right">
                <div ref={impactTypeRef} className="tour-host">
                  <ImpactStatisticsView viewParam={viewSize.impactStatisticsView} panelParam={panelStyle} viewName={"Impact Type View"} />
                </div>
                <div ref={entityRef} className="tour-host">
                  <ImpactEntityView viewParam={viewSize.impactEntityView} panelParam={panelStyle} viewName={"Impact Entity View"} />
                </div>
              </div>
            </div>
          </Content>
        </Layout>
      </Layout>

      {/* AntD Tour with labeled buttons */}
      <Tour
        open={isTourOpen}
        // mask={false}        // ← 加这行
        steps={enhancedSteps}
        current={tourStep}
        onChange={(curr) => { setTourStep(curr); }}
        onClose={() => { setIsTourOpen(false); setTourStep(0); }}
      />

      {/* Help Modal */}
      <Modal
        title="Help Information"
        closable={true}
        open={isHelpModalOpen}
        onOk={handleHelpModalOk}
        onCancel={handleHelpModalCancel}
        width="60vw"
        height="50vh"
        // bodyStyle={{ height: '20vh', overflow: 'auto' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Browser Recommendation */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Recommended Browsers</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              For the best experience, please use
              <img src={chromeLogoUrl} alt="Chrome" style={{ height: '20px', margin: '0 4px' }} />
              or
              <img src={firefoxLogoUrl} alt="Firefox" style={{ height: '20px', margin: '0 4px' }} />
              .
            </div>
          </div>

          {/* Project Background */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Background</div>
            <div>This project aims to visualize the broad impact of science and science funding. Please view the demo video for more details:</div>
            <div className="tour-media" style={{ marginTop: 8 }}>
              <video src={mediaByStep[9].src} controls autoPlay loop muted playsInline />
            </div>
          </div>

          {/* Data Coverage Info */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Data Coverage</div>
            <div>The current demo system displays 20% of the full NIH dataset. Accessing the complete dataset may require longer calculation time but can be provided upon request.</div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Homepage;
